import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchNotification,
  hrefForSubject,
  markRead,
} from '../api/notifications'
import { useCan, useSession } from '../useSession'

/**
 * Where a tapped notification lands (D-530).
 *
 * **A server route rather than a link the push carries directly**, because
 * resolving a notification needs two things the service worker does not have:
 * the reader's capabilities, and a second request. `hrefForSubject` sends a
 * parent and a teacher to different screens for the same message (D-324) — a
 * URL minted when the message was sent would name the wrong screen for one of
 * them. So the push carries an id, and the running app decides. (Until D-553
 * it also looked up a private lesson's week, D-388; a private's messages are
 * the envelope's now and land where the row is answered.)
 *
 * **It never dead-ends.** A notification that cannot be found — cleared, deleted,
 * somebody else's — lands on Home rather than on an error, because the person
 * tapped a banner about their own studio and being told "no" is the one useless
 * answer.
 */
export default function NotificationLink() {
  const { id } = useParams<{ id: string }>()
  const { session, isLoading } = useSession()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canViewPeople = useCan('ViewPeople')

  const { data: row, isPending, isError } = useQuery({
    queryKey: ['notification', id],
    queryFn: () => fetchNotification(id!),
    enabled: Boolean(id) && Boolean(session),

    // One shot. A retry here is a second wait on a screen that exists only to
    // redirect, and Home is a perfectly good answer.
    retry: false,
  })

  useEffect(() => {
    if (!row) return

    let alive = true

    void (async () => {
      /*
       * Marked read on the way through. She has seen it — it was on her lock
       * screen and she tapped it — and a badge that keeps counting something
       * she has acted on is the failure D-324 names.
       */
      if (!row.isRead) {
        try {
          await markRead(row.id)
          queryClient.invalidateQueries({ queryKey: ['notifications'] })
        } catch {
          // Landing in the right place matters more than the count.
        }
      }

      if (!alive) return

      navigate(
        hrefForSubject(row, { canViewPeople, isParent: session?.isParent, teaches: session?.teaches }) ?? '/',
        { replace: true },
      )
    })()

    return () => {
      alive = false
    }
  }, [row, navigate, queryClient, canViewPeople, session?.isParent, session?.teaches])

  if (isLoading) return null

  /*
   * **Signed out, so it says where to come back to.** A push is tapped hours
   * later, often on a phone whose session has expired — without `next` she signs
   * in and lands on Home, having lost the thing she opened the app for.
   */
  if (!session) {
    return <Navigate to={`/signin?next=${encodeURIComponent(`/n/${id}`)}`} replace />
  }

  if (isError) return <Navigate to="/" replace />

  // Deliberately blank rather than a spinner: this resolves in one request and
  // a flash of "Loading..." on the way somewhere is worse than nothing.
  return isPending || row ? null : <Navigate to="/" replace />
}
